import type { DataStateChangeEventArgs } from '@syncfusion/ej2-angular-grids';

/** The shapes of the grid's `action` argument that matter here; the grid does not type it. */
type FilterPopupAction = {
    requestType?: string;
    /** The column the popup belongs to. */
    columnName?: string;
    filterModel?: { options?: { field?: string } };
    /** What the user typed into a checkbox filter's search box. */
    value?: unknown;
};

/** Loads every value of a data-driven column, for its checkbox filter list. */
export type FilterValueLoader = (field: string) => Promise<string[]>;

/**
 * Requests a server-bound grid raises through `dataStateChange` for the contents of a filter popup. They are not
 * page loads and must be answered through `state.dataSource`; the grid waits for the answer and keeps its spinner
 * until it comes. Syncfusion spells them inconsistently (`filterSearchBegin` against `filterchoicerequest`), so they
 * are compared in lower case.
 */
const FILTER_POPUP_REQUESTS = new Set(['filterchoicerequest', 'filtersearchbegin', 'stringfilterrequest']);

function requestTypeOf(state: DataStateChangeEventArgs): string {
    return ((state.action as FilterPopupAction | undefined)?.requestType ?? '').toLowerCase();
}

/** True when the grid is asking for the contents of a filter popup rather than for a page of rows. */
export function isFilterPopupRequest(state: DataStateChangeEventArgs): boolean {
    return FILTER_POPUP_REQUESTS.has(requestTypeOf(state));
}

/**
 * Answers a filter popup request: the values for a checkbox list (narrowed by the search text when the user typed
 * one, because the grid shows exactly what it is given), or nothing for the suggestions of a text column's menu
 * filter, which the server does not supply.
 */
export function answerFilterPopup(state: DataStateChangeEventArgs, loadValues: FilterValueLoader): void {
    const respond = state.dataSource as ((data: Record<string, string>[]) => void) | undefined;
    if (!respond) return;

    const action = state.action as FilterPopupAction | undefined;
    const field = action?.columnName ?? action?.filterModel?.options?.field ?? state.select?.[0];
    const type = requestTypeOf(state);
    if (type === 'stringfilterrequest' || !field) {
        respond([]);
        return;
    }

    const search = type === 'filtersearchbegin' ? String(action?.value ?? '').trim().toLowerCase() : '';
    loadValues(field)
        .then(values => respond(values
            .filter(value => !search || value.toLowerCase().includes(search))
            .map(value => ({ [field]: value }))))
        .catch(() => respond([]));
}
